import React, {useContext, useMemo, useCallback, useEffect} from "react";
import {ResortsContext} from "../store/providers/ResortsProvider";
import {LocaleContext} from "../store/providers/LocaleProvider";
import {useCountries} from "./useCountries";
import {getGroupedByCountry, transformDestinations} from "../selectors/resorts";
import {CountryIcon, StreamlineIcon} from "@hapinest/theme";
import {RESORTS} from "../common/routes";
import Earth1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/maps-navigation/earth/earth-1.svg";
import BusinessDealHandshakeCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/business-products/business-deals/business-deal-handshake-circle.svg";
import {useI18N} from "../common/common";

export const useResortsMenu = (iconClassname, includeCountryLink = false) => {
    const [resortsStateAndIds, resortsDispatch] = useContext(ResortsContext);
    const {partnerResorts, nonPartnerResorts, resortsLoading} = resortsStateAndIds;
    const {locale, localeUppercaseUnderscore} = useContext(LocaleContext)?.[0];
    const {countries} = useCountries();
    const {i18n} = useI18N();

    useEffect(() => {
        if (partnerResorts && !partnerResorts.length && nonPartnerResorts && !nonPartnerResorts.length && !resortsLoading) {
            resortsDispatch({type: "GET_RESORTS_INIT", payload: {locale: localeUppercaseUnderscore, is_bookable: true}});
        }
    }, [partnerResorts, nonPartnerResorts]);

    const getCountryCode = useCallback((country) => {
        for (const [key, value] of countries?.entries()) {
            if (value === country) {
                return key;
            }
        }
        return null;
    }, [countries]);

    const getCountryUrl = useCallback((countrySlug) => {
        if (locale) {
            return `${location.origin}/${locale}/${countrySlug}/`;
        }
    }, [location, locale]);

    const menuItems = useMemo(() => {
        if (partnerResorts && nonPartnerResorts) {
            const groupedByCountry = getGroupedByCountry(transformDestinations(nonPartnerResorts.concat(partnerResorts))) ?? [];

            const partnerResortsList = partnerResorts.map((item) => {
                const countryUrl = getCountryUrl(item?.destination?.country?.slug);
                return {
                    text: item?.resortName + ", " + item?.destination?.country?.country,
                    href: `${countryUrl}${item?.destination?.slug}/${item?.resortSlug}/`,
                    resortId: item?.hapimagResortId
                };
            });

            const items = Object.keys(groupedByCountry)?.map(country => {
                const countryUrl = getCountryUrl(groupedByCountry[country]?.[0]?.countrySlug);

                const subItems = groupedByCountry[country].map(item => {
                    return {
                        text: item?.destination,
                        href: `${countryUrl}${item?.destinationSlug}/${item?.resorts?.[0]?.resortSlug}/`
                    };
                });

                if (includeCountryLink && subItems?.length > 1) {
                    subItems.unshift({
                        text: i18n("view.resorts_menu.all_resorts_in_country", {country}),
                        href: countryUrl
                    });
                }

                if (subItems.length === 0) {
                    return undefined;
                }

                return {
                    text: country,
                    icon: (
                        <CountryIcon
                            className={iconClassname}
                            countryCode={getCountryCode(country)}
                        />
                    ),
                    href: countryUrl,
                    subItems
                };
            }).filter(((item) => item));

            const menuItemsList = [
                {
                    text: i18n("view.booking_information.all_locations"),
                    icon: <StreamlineIcon className={iconClassname}><Earth1 /></StreamlineIcon>,
                    href: `${location.origin}/${locale}${RESORTS}/`
                }
            ];

            if (partnerResortsList.length > 0) {
                menuItemsList.push(
                    {
                        text: i18n("view.booking_information.partner_resorts"),
                        icon: <StreamlineIcon className={iconClassname}><BusinessDealHandshakeCircle /></StreamlineIcon>,
                        subItems: partnerResortsList,
                        href: `/${locale}/${i18n("view.booking_information.partner_resorts.slug")}`,
                        isPartnerResortsList: true
                    }
                );
            }

            menuItemsList.push(...items);
            return menuItemsList;
        } else {
            return [];
        }
    }, [getCountryUrl, getCountryCode, partnerResorts, nonPartnerResorts, includeCountryLink]);

    return {
        menuItems
    };
};
